import React, { useCallback, useEffect, useState } from 'react';
import { OptionItem, OptionList } from '@components/common/dropdown';
import ChevronDown from '@assets/svgs/ChevronDownBold.svg';
import ChevronUp from '@assets/svgs/ChevronUp.svg';
import type { OptionItem as OptionItemType } from '@steadioTypes/dropdown';

type FilterProps = {
  initialValue?: string;
  optionList: OptionItemType[];
  onChange: (value: string) => void;
};

export default function Filter({
  initialValue,
  optionList,
  onChange,
}: FilterProps) {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [filterLabel, setFilterLabel] = useState<React.ReactNode>(
    optionList[0].description,
  );

  const setFilterLabelByValue = useCallback(
    (newValue: string | null | undefined) => {
      if (newValue === null || newValue === undefined) {
        return;
      }

      setFilterLabel(
        optionList.filter(({ value }) => value === newValue)[0].description,
      );
    },
    [optionList],
  );

  useEffect(() => {
    setFilterLabelByValue(initialValue);
  }, [initialValue, setFilterLabelByValue]);

  const openOptions = () => setIsOpen(true);

  const closeOptions = (event: React.FocusEvent<HTMLButtonElement>) => {
    setIsOpen(false);
    const optionValue = event.relatedTarget?.getAttribute('data-option-value');
    setFilterLabelByValue(optionValue);
    if (optionValue !== null && optionValue !== undefined) {
      onChange(optionValue);
    }
  };

  // FIXME: safari에서 button을 mouse로 클릭하더라도 focus 이벤트가 동작하지 않아 추가
  const onFilterClick = (event: React.MouseEvent<HTMLButtonElement>) =>
    event.currentTarget.focus();

  return (
    <div className="relative">
      <button
        type="button"
        className={`h-[32px] px-[16px] py-[8px] surface_primary border ${filterBorderColor(
          isOpen,
        )} rounded-[16px] flex items-center max-w-[164px] outline-none`}
        onClick={onFilterClick}
        onFocus={openOptions}
        onBlur={closeOptions}
      >
        <span className="content_secondary font_label_medium_lg text-ellipsis overflow-hidden whitespace-nowrap">
          {filterLabel}
        </span>
        {isOpen ? (
          <ChevronUp className={`${filterArrowStyle}`} />
        ) : (
          <ChevronDown className={`${filterArrowStyle}`} />
        )}
      </button>
      {isOpen && (
        <div className="absolute top-[42px] right-0 z-10 min-w-[120px] max-w-[280px]">
          <OptionList>
            {optionList.map((optionItem) => (
              <OptionItem key={optionItem.value} optionItem={optionItem} />
            ))}
          </OptionList>
        </div>
      )}
    </div>
  );
}

const filterBorderColor = (isOpen: boolean) =>
  isOpen ? 'border_accent' : 'border_black_opacity';

const filterArrowStyle = 'shrink-0 ml-[4px] w-[10px] h-[10px]';
